import * as React from 'react';
import { cn } from '../utils';
import type { BookLine, LimitKind } from '@sharpr/types';
import { presentationFor } from './books';
import { formatOdds } from './format';

// Liquidity (bet-limit) domain logic. Lives apart from LineDisplay.tsx so the
// barrel can export these constants without carrying LineDisplay's static
// TeaseSurface → RichPopover → vaul chain — consumers that only need a
// threshold or tier class must not depend on export-level tree-shaking to
// avoid the ~33 KB gz dialog stack.

export type LiquidityTier = 'dead' | 'thin' | 'ok' | 'deep';
export interface LiquidityThresholds {
  /** Below this → red. */
  orange: number;
  /** Must reach this to be yellow. */
  yellow: number;
  /** Must reach this to be green. */
  green: number;
}
export const DEFAULT_LIQUIDITY_THRESHOLDS: LiquidityThresholds = { orange: 1, yellow: 100, green: 200 };

/**
 * Tier thresholds (USD) for bet-limit severity dots. Three user-set cutoffs
 * (orange, yellow, green); red is implicit below `orange`.
 */
export function betLimitTier(
  limit: number,
  thresholds: LiquidityThresholds = DEFAULT_LIQUIDITY_THRESHOLDS,
): LiquidityTier {
  if (limit < thresholds.orange) return 'dead';
  if (limit < thresholds.yellow) return 'thin';
  if (limit < thresholds.green) return 'ok';
  return 'deep';
}

export const TIER_DOT_CLASS: Record<LiquidityTier, string> = {
  dead: 'bg-red-500',
  thin: 'bg-orange-500',
  ok: 'bg-yellow-400',
  deep: 'bg-emerald-500',
};

/** An exchange's available liquidity as the viewer reads it. A sportsbook's max bet is not shown. */
export function limitLabel(limitKind: LimitKind | null, amount: number | undefined): string | undefined {
  if (limitKind !== 'liquidity' || amount === undefined) return undefined;
  return `liq $${amount.toLocaleString('en-US')}`;
}

/** Only an exchange's liquidity is graded into tiers (a sportsbook's max bet is not shown). */
export function showsLiquidityTier(limitKind: LimitKind | null): boolean {
  return limitKind === 'liquidity';
}

/**
 * Build the tooltip content for a line's limit. Returns null when the book has
 * no limit kind or the line no limit. Callers decide where to render the
 * tooltip and what wrapper to use for the hover target.
 */
export function getLiquidityTooltip(line: BookLine, book: { name: string; limitKind: LimitKind | null }): React.ReactNode | null {
  const label = limitLabel(book.limitKind, line.betLimit);
  if (label === undefined) return null;
  const sideChar = line.side === 'over' ? 'o' : 'u';
  const oddsStr = formatOdds(line.odds);
  const presentation = presentationFor(line.book, book.name);
  return (
    <span className="inline-flex items-center gap-1.5">
      {presentation.thumbnail && (
        <img
          src={presentation.thumbnail}
          alt={book.name}
          className={cn('h-4 w-4 rounded object-cover', presentation.thumbnailScale)}
        />
      )}
      <span>
        <span className="font-mono">{label}</span>
        {' @ '}
        <span className="font-mono">
          {sideChar}{line.line.toFixed(1)} ({oddsStr})
        </span>
      </span>
    </span>
  );
}
